import { CircularProgress, useTheme, Box } from "@mui/material"
import { SyntheticEvent, useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import { BaseAutocomplete } from "./BaseAutocomplete"
import { AsyncDropdownProps } from "./types"

export function AsyncDropdown<T, M extends boolean | undefined = undefined>({
	getter,
	loading,
	onClose,
	data,
	textFieldProps,
	hideLabel,
	onChange,
	value,
	multiple,
	required,
	getOptionLabel,
	isOptionEqualToValue,
	clearable = true,
	startAdornment: customStartAdornment,
	getOptionDisabled,
	...autocompleteProps
}: AsyncDropdownProps<T, M>) {
	const theme = useTheme()
	const [lastEvent, setLastEvent] = useState<SyntheticEvent<Element, Event> | null>(null)
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

	const handleOpen = (event: SyntheticEvent<Element, Event>) => {
		setLastEvent(event)
	}

	useEffect(() => {
		if (!lastEvent) return

		if (debounceRef.current) {
			clearTimeout(debounceRef.current)
		}

		debounceRef.current = setTimeout(() => {
			getter()
		}, 100)

		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current)
		}
	}, [lastEvent, getter])

	const loadingIndicator = loading ? (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
		>
			<CircularProgress
				size={16}
				thickness={2}
				sx={{ color: theme.palette.primary.main, mr: 1 }}
			/>
		</motion.div>
	) : null

	const effectiveStartAdornment = loading ? loadingIndicator : customStartAdornment

	return (
		<Box sx={{ width: "100%", height: "100%" }}>
			<BaseAutocomplete
				multiple={multiple}
				getOptionLabel={getOptionLabel}
				hideLabel={hideLabel}
				isOptionEqualToValue={isOptionEqualToValue}
				value={value}
				required={required}
				loading={loading}
				onOpen={handleOpen}
				onClose={onClose}
				onChange={onChange}
				data={data}
				startAdornment={effectiveStartAdornment}
				textFieldProps={textFieldProps}
				clearable={clearable}
				noOptionsText={loading ? "Loading..." : "No options"}
				getOptionDisabled={getOptionDisabled}
				{...autocompleteProps}
			/>
		</Box>
	)
}
